import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { BillingInfoFormData, BillingInfoFormHandlers, BillingInfoFormValues } from '@ctct/application-layer';
import { type BillingStrategyQuery } from '@ctct/domain-layer';
import { useOnBeforeUnload } from '@ctct/presentation-layer';

import { BillingInfoForm } from './components/BillingInfoForm';
import { TaxInfoUpdateDetector } from './components/TaxInfoUpdateDetector';

interface Props {
  data: BillingInfoFormData;
  handlers: BillingInfoFormHandlers;
  billingStrategy: BillingStrategyQuery;
}

export const FormWithinContext = ({ data, handlers, billingStrategy }: Props) => {
  const { setValue, getValues, trigger, formState, clearErrors } = useFormContext<BillingInfoFormValues>();

  const zipcode = useWatch<BillingInfoFormValues>({ name: 'zipcode' }) as string | undefined;
  const selectedCountryCode = useWatch<BillingInfoFormValues>({ name: 'country' }) as string | undefined;

  useEffect(() => {
    if (zipcode) {
      handlers.onZipCodeChange(zipcode, { setValue, getValues });
    }
  }, [handlers, zipcode, getValues, setValue]);

  useEffect(() => {
    handlers.addTriggerValidation(() => trigger(undefined, { shouldFocus: true }));
  }, [handlers, trigger]);

  useEffect(() => {
    handlers.addGetFormValues(getValues);
  }, [handlers, getValues]);

  useEffect(() => {
    handlers.addSetFormValue(setValue);
  }, [handlers, setValue]);

  useEffect(() => {
    handlers.addClearErrors(clearErrors);
  }, [handlers, clearErrors]);

  useEffect(() => {
    handlers.onIsDirtyChange(formState.isDirty);
  }, [handlers, formState.isDirty]);

  useEffect(() => {
    if (selectedCountryCode) {
      handlers.onCountryChange(selectedCountryCode, { setValue, defaultValues: data.getDefaultValues() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode]);

  // persist form data on unmount or before unload
  useOnBeforeUnload(handlers.persistBillingFormData);
  useEffect(() => () => handlers.persistBillingFormData(), [handlers]);

  const copy = data.getFormWidgetCopies();

  return (
    <>
      <BillingInfoForm
        copy={copy}
        // countries
        countries={data.getCountries()}
        // states
        isShowingStates={data.isShowingStates(selectedCountryCode)}
        states={data.getStates(selectedCountryCode)}
        statePlaceholder={data.getStatePlaceholder(selectedCountryCode)}
        // emails
        emailList={data.getEmailList()}
        // vat
        showTaxAgreement={data.isShowingTaxAgreement(selectedCountryCode)}
        showTaxNumber={data.isShowingTaxNumber(selectedCountryCode)}
        taxDisclaimer={data.getTaxDisclaimer(selectedCountryCode)}
        taxLabel={data.getTaxLabel(selectedCountryCode)}
      />
      <TaxInfoUpdateDetector onTaxInfoUpdate={handlers.onTaxInfoUpdate} billingStrategy={billingStrategy} />
    </>
  );
};
